/**
 * Static copy tables for the Math Engine generation loader and the
 * resume panel. Kept separate from any stage component so adding a new
 * stage is one obvious place to edit.
 */
import React from "react";

// Resume panel — given a saved artifact's output_type, what step number
// and label do we show in the "in-progress" tile?
export const STAGE_PROGRESS = {
  math_activation: { idx: 1, label: "Activation done" },
  math_diagnostic: { idx: 2, label: "Diagnostic done" },
  math_lesson1: { idx: 3, label: "Lesson 1 done" },
  math_exit_review: { idx: 4, label: "Exit review done" },
  math_lesson2: { idx: 5, label: "Lesson taught" },
  math_unit_summary: { idx: 9, label: "Unit wrapped" },
};

// Generation loader — what to render in the spinner card while a stage
// is still in flight. `body(unit, mini)` is a function so we can splice
// the live unit/mini into the copy inline.
export const STAGE_COPY = {
  activation: {
    label: "Stage 1 of 9",
    title: "Generating the Activation Activity",
    body: (unit, mini) => (
      <>
        Reading the SCDSB scope and sequence for {unit.grade} ·{" "}
        {mini ? (
          <>
            <span className="font-bold">
              {mini.label} {mini.name}
            </span>{" "}
            (inside Unit {unit.unit_number} — {unit.name}),
          </>
        ) : (
          <>
            Unit {unit.unit_number} —{" "}
            <span className="font-bold">{unit.name}</span>,
          </>
        )}{" "}
        consulting the rich task library and misconceptions reference, and
        drafting a 15-20 minute activation that surfaces what students already
        know.
      </>
    ),
  },
  diagnostic: {
    label: "Stage 2 of 9",
    title: "Generating the Diagnostic",
    body: (unit, mini) => (
      <>
        Designing 5 multiple-choice questions for {unit.grade} ·{" "}
        {mini ? (
          <>
            <span className="font-bold">
              {mini.label} {mini.name}
            </span>{" "}
          </>
        ) : (
          <>
            Unit {unit.unit_number} —{" "}
            <span className="font-bold">{unit.name}</span>{" "}
          </>
        )}
        against the Four Golden Rules. Every distractor maps to a named
        misconception from the misconceptions library.
      </>
    ),
  },
  lesson1: {
    label: "Stage 3 of 9",
    title: "Designing Adaptive Lesson 1",
    body: (unit, mini) => (
      <>
        Reading your class results, mapping each option onto its named
        misconception, identifying the dominant pattern, then drafting a
        three-part lesson (Minds On / Action / Consolidation) with three
        differentiated paths — Mastered, Developing, and Need Re-teach — for{" "}
        {mini ? (
          <span className="font-bold">
            {mini.label} {mini.name}
          </span>
        ) : (
          <>
            Unit {unit.unit_number} —{" "}
            <span className="font-bold">{unit.name}</span>
          </>
        )}
        .
      </>
    ),
  },
  exit_review: {
    label: "Stage 5 of 9",
    title: "Reading the exit tickets",
    body: (unit, mini) => (
      <>
        Mapping the three buckets onto Lesson 1's dominant misconception,
        computing tomorrow's call (move on / hold this ground / re-teach),
        and writing a concrete next-day plan for{" "}
        {mini ? (
          <span className="font-bold">
            {mini.label} {mini.name}
          </span>
        ) : (
          <>
            Unit {unit.unit_number} —{" "}
            <span className="font-bold">{unit.name}</span>
          </>
        )}
        .
      </>
    ),
  },
  lesson2: {
    label: "Adaptive lesson",
    title: "Designing the next adaptive lesson",
    body: (unit, mini) => (
      <>
        Branching on yesterday's recommendation, picking the right
        framing (move on / second pass / re-teach), and drafting the
        next lesson with the same hardened three-path structure for{" "}
        {mini ? (
          <span className="font-bold">
            {mini.label} {mini.name}
          </span>
        ) : (
          <>
            Unit {unit.unit_number} —{" "}
            <span className="font-bold">{unit.name}</span>
          </>
        )}
        .
      </>
    ),
  },
  unit_summary: {
    label: "End-of-unit summary",
    title: "Writing the unit retrospective",
    body: (unit, mini) => (
      <>
        Reading every lesson and exit ticket you logged for{" "}
        {mini ? (
          <span className="font-bold">
            {mini.label} {mini.name}
          </span>
        ) : (
          <>
            Unit {unit.unit_number} —{" "}
            <span className="font-bold">{unit.name}</span>
          </>
        )}
        {" "}and drafting a one-page printable summary — WALTs taught,
        expectations covered, exit-ticket trend, and pacing.
      </>
    ),
  },
};
